import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@trillion/ui/components/ui/dialog";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Loader2, Play } from "lucide-react";
import { firstFieldErrors, zodToFieldErrors } from "@trillion/validation";
import { triggerJobRequestSchema } from "@/validation/trigger-job-request";

interface TriggerDialogProps {
  /**
   * Issues the trigger. Resolves TRUE when it was accepted (202) and FALSE when it was rejected — it
   * never rejects, because the row's `Run now` fires the same handler from an async onClick and
   * discards the result (a rejecting promise there would be an unhandled rejection).
   *
   * The dialog closes and resets only on `true`, so a 409 leaves it open with the page's error toast —
   * the behaviour today's throwing call already produces. JobsPage owns the toasts, the `triggering`
   * marking and the pending overlay, because it is the only place that can mark the row pending BEFORE
   * the request goes out.
   */
  onTrigger: (recurringJobId: string) => Promise<boolean>;
}

// Ad-hoc trigger of a recurring job by id. Validates client-side with the same
// Zod schema the API enforces (sc-86949) before the POST — so an empty id surfaces
// inline with no network call.
export function TriggerDialog({ onTrigger }: TriggerDialogProps) {
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    const result = triggerJobRequestSchema.safeParse({ recurring_job_id: jobId });
    if (!result.success) {
      setError(firstFieldErrors(zodToFieldErrors(result.error)).recurring_job_id);
      return;
    }

    setSubmitting(true);
    try {
      const accepted = await onTrigger(result.data.recurring_job_id);
      if (accepted) {
        setOpen(false);
        setJobId("");
        setError(undefined);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setError(undefined);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Play className="size-4" />
          Trigger job
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trigger a recurring job</DialogTitle>
          <DialogDescription>
            Fire an immediate run of a registered recurring job by its id.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="recurring_job_id">Recurring job id</Label>
          <Input
            id="recurring_job_id"
            value={jobId}
            placeholder="heartbeat"
            onChange={(e) => {
              setJobId(e.target.value);
              setError(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" />}
            Trigger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
